const { isPsoCode, isbnToEan } = require("./bookIdentifiers");
const positive = (value) => {
  const number = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(number) && number > 0 ? number : 0;
};
const round = (value, digits = 2) => Number(Number(value).toFixed(digits));
const publicationYear = () =>
  Number(
    new Intl.DateTimeFormat("en", { timeZone: "America/Fortaleza", year: "numeric" }).format(
      new Date()
    )
  );

function getBookPhysicalData(book = {}) {
  const pages = positive(book.pdf?.pages);
  const widthCm = positive(book.pdf?.widthCm);
  const heightCm = positive(book.pdf?.heightCm);
  const grammage = positive(book.paper?.grammage);
  const thicknessMicron = positive(book.paper?.thicknessMicron);
  const estimatedWeightGr =
    pages && widthCm && heightCm && grammage
      ? Math.ceil((widthCm / 100) * (heightCm / 100) * (pages / 2) * grammage)
      : 0;
  const estimatedDepthCm =
    pages && thicknessMicron ? round(((pages / 2) * thicknessMicron) / 10000, 3) : 0;
  return {
    pages,
    widthCm,
    heightCm,
    estimatedWeightGr,
    estimatedDepthCm,
    weightGr: positive(book.weightGr) || estimatedWeightGr,
    depthCm: positive(book.depthCm) || estimatedDepthCm,
    weightEstimated: !positive(book.weightGr),
    depthEstimated: !positive(book.depthCm),
  };
}

function getPublicationValidationErrors(book, { editing = false } = {}) {
  const errors = {};
  if (!editing && !isPsoCode(book.internalCode))
    errors.internalCode = "Gere o código PSO da obra antes de publicar.";
  if (!editing && !["pso", "isbn"].includes(book.identificationMode))
    errors.identificationMode = "Selecione se a obra possui ISBN.";
  if (!editing && book.identificationMode === "pso" && String(book.isbn || "").trim())
    errors.identificationMode = "Selecione a opção com ISBN para informar um ISBN.";
  if (!editing && book.identificationMode === "isbn" && !isbnToEan(book.isbn))
    errors.isbn = "Informe um ISBN válido com 10 ou 13 caracteres.";
  if (!String(book.title || "").trim()) errors.title = "Informe o título da obra.";
  if (String(book.title).trim().length > 255)
    errors.title = "O título da obra deve ter até 255 caracteres.";
  const price = String(book.price ?? "")
    .trim()
    .replace(",", ".");
  if ((!editing || price) && (!/^\d{1,8}(\.\d{1,2})?$/.test(price) || Number(price) <= 0))
    errors.price =
      "Informe o preço de venda maior que zero, com até duas casas decimais (ex.: 49,90).";
  if (!String(book.author || "").trim()) errors.author = "Informe o nome do autor da obra.";
  if (String(book.author).trim().length > 100)
    errors.author = "O nome do autor deve ter até 100 caracteres.";
  if (!/^\d{4}$/.test(String(book.publicationYear)) || Number(book.publicationYear) < 1000)
    errors.publicationYear = "Informe o ano de publicação com quatro dígitos.";
  if (
    !/^\d+$/.test(String(book.edition)) ||
    Number(book.edition) < 1 ||
    Number(book.edition) > 9999
  )
    errors.edition = "Informe uma edição inteira entre 1 e 9999.";
  for (const [field, label] of [
    ["finish", "acabamento"],
    ["language", "idioma"],
    ["formatType", "tipo de formato"],
  ]) {
    if (!String(book[field] || "").trim() || String(book[field]).length > 100)
      errors[field] = `Informe ${label} com até 100 caracteres.`;
  }
  if (String(book.publisher || "").length > 100)
    errors.publisher = "O selo editorial deve ter até 100 caracteres.";
  if (!["ISBN", "BISAC"].includes(book.classificationType))
    errors.classificationType = "Selecione o tipo de código de categorização.";
  if (book.classificationType === "ISBN" && !String(book.isbn || "").trim())
    errors.classificationType = "Informe o ISBN ou escolha o tipo de código BISAC.";
  if (String(book.bisacSubject || "").length > 100)
    errors.bisacSubject = "O assunto BISAC deve ter até 100 caracteres.";
  for (const field of ["weightGr", "depthCm"]) {
    if (book[field] !== undefined && book[field] !== "" && !positive(book[field]))
      errors[field] = "Peso e profundidade devem ser números maiores que zero.";
  }
  const physical = getBookPhysicalData(book);
  if (
    !editing &&
    (!Number.isInteger(physical.pages) ||
      !physical.pages ||
      !physical.widthCm ||
      !physical.heightCm)
  )
    errors.pdf = "Envie um PDF válido para detectar páginas e dimensões.";
  if (!editing && (!physical.weightGr || !physical.depthCm))
    errors.physical = "Informe o peso e a profundidade ou selecione um papel para estimá-los.";
  if (book.publicationDetailsConfirmed !== true)
    errors.publicationDetailsConfirmed = "Confirme que revisou os dados da obra antes de publicar.";
  return errors;
}

function getPublicationValidationError(book, options) {
  return Object.values(getPublicationValidationErrors(book, options))[0] || "";
}

function getBookPropertyValues(book, shippingData) {
  const languageMap = {
    pt: "Português",
    "pt-br": "Português",
    en: "Inglês",
    "en-us": "Inglês",
    es: "Espanhol",
  };
  const language =
    languageMap[String(book.language || "pt-BR").toLowerCase()] || String(book.language);
  return [
    {
      name: "Acabamento",
      value: book.finish || (positive(book.pdf?.pages) > 56 ? "Brochura" : "Grampeado"),
    },
    { name: "Altura", value: String(round(shippingData.height)) },
    { name: "Ano de publicação", value: String(book.publicationYear || publicationYear()) },
    { name: "Autor", value: String(book.author || "").trim() },
    { name: "Edição", value: String(book.edition || "1") },
    { name: "Idioma", value: language },
    { name: "Código PSO", value: book.internalCode || "" },
    { name: "ISBN", value: String(book.isbn || "").trim() },
    { name: "Largura", value: String(round(shippingData.width)) },
    { name: "Páginas", value: String(positive(book.pdf?.pages)) },
    { name: "Peso", value: String(Math.ceil(shippingData.weight)) },
    { name: "Profundidade", value: String(round(shippingData.length, 3)) },
    { name: "Selos Editoriais", value: String(book.publisher || "").trim() },
    {
      name: "Tipo de assunto BISAC",
      value: book.classificationType === "ISBN" ? "" : String(book.bisacSubject || "Geral"),
    },
    { name: "Tipo de código de categorização", value: book.classificationType || "BISAC" },
    { name: "Tipo de formato", value: book.formatType || "Capa comum" },
    { name: "Tipo de Idioma", value: language },
  ].filter((property) => property.value);
}

module.exports = {
  positive,
  publicationYear,
  getBookPhysicalData,
  getPublicationValidationError,
  getPublicationValidationErrors,
  getBookPropertyValues,
};
